import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { v4 } from "uuid";
import { ProductItemProps } from "src/lib/components/product-item/default/common";
import { toast } from "react-toastify";

export const HEADER_TENANT = "x-Tenant-Id";
export const HEADER_SALE_CHANNEL = "x-Sale-Channel";
export const COOKIE_GLOBAL_NAME = "_sgd_";

export enum ORDER_PAYMENT_METHOD {
  Cash = 0,
  Bank = 1,
  Card = 2,
  DigitalWallets = 3,
}
export const ORDER_PAYMENT_LABEL: { [k: number]: string } = {
  [ORDER_PAYMENT_METHOD.Cash]: "Thanh toán khi nhận hàng",
  [ORDER_PAYMENT_METHOD.Bank]: "Thanh toán bằng Thẻ ngân hàng",
  [ORDER_PAYMENT_METHOD.Card]: "Thanh toán bằng Thẻ tín dụng/Ghi nợ",
  [ORDER_PAYMENT_METHOD.DigitalWallets]: "Thanh toán qua Ví điện tử",
};
export enum ORDER_PAYMENT_VENDOR {
  MOMO = 0,
  VNPAY = 1,
  APPOTAPAY = 2,
}
export enum ORDER_STATUS {
  New = 0,
  Pending = 2,
  Authorized = 4,
  Completed = 6,
  Declined = 8,
  Refunded = 10,
  Failed = 12,
  Chargeback = 14,
  Cancelled = 16,
  Expired = 18,
  Confirmed = 20,
}

export interface Store {
  id: string;
  code: string;
  name: string;
  description: string;
  address: string;
  phone: string;
  email: string;
}

export interface Location {
  id: number;
  code: string;
  name: string;
  fullName: string;
  displayOrder: number;
}

export interface GlobalValue {
  cart?: string;
  orgId?: string;
  tenantId?: string;
  saleChannelId?: string;
}

const axiosClient = axios.create();
const timer: Record<string, { args: any[]; timer: any }> = {};
const isClientSide = !!globalThis.window;

export class ClientFetcher {
  protected locationBaseURL: string = "";
  protected cartBaseURL: string = "";
  protected orderBaseURL: string = "";
  protected mediaBaseURL: string = "";
  protected dashBaseURL: string = "";
  protected _global: any;
  protected get global(): GlobalValue {
    return this._global;
  }

  public get cookieValue() {
    return (this.client.defaults.env as any).cookieValue;
  }

  public get updateCookie() {
    return (this.client.defaults.env as any).updateCookie;
  }

  public set tenantId(value) {
    this.client.defaults.headers.common[HEADER_TENANT] = value;
    this.updateCookie?.({
      tenantId: value,
    });
  }
  public get tenantId() {
    return this.client.defaults.headers.common[HEADER_TENANT];
  }

  public set saleChannelId(value) {
    this.client.defaults.headers.common[HEADER_SALE_CHANNEL] = value;
    this.updateCookie?.({
      saleChannelId: value,
    });
  }
  public get saleChannelId() {
    return this.client.defaults.headers.common[HEADER_SALE_CHANNEL];
  }

  public set cart(value: string | null) {
    (this.client.defaults.env as any).cart = value || null;
    this.updateCookie?.({
      cart: (this.client.defaults.env as any).car,
    });
  }
  public get cart(): string | null {
    return (this.client.defaults.env as any).cart || null;
  }

  protected fetch(options: { url: string } & AxiosRequestConfig<any>) {
    return this.client(options);
  }

  protected withResult<T>(
    promise: Promise<T>,
    defaultValue?: T,
  ): Promise<{ value: T; error?: any }> {
    return promise.then(
      (value) => ({ value }),
      (error) => ({ value: defaultValue, error }) as any,
    );
  }

  protected withCache<T>(
    name: string,
    callback: () => Promise<{ error?: any; value: T }>,
  ): Promise<T> {
    if (!globalThis.window) return callback().then(({ value }) => value);
    const storage = localStorage;
    let cache = storage.getItem(name);
    // prettier-ignore
    if (cache) try { cache = JSON.parse(cache).v } catch (_) { cache = null }
    if (cache) return cache as any;
    return callback().then(({ error, value: v }: any) => {
      if (!error) storage.setItem(name, JSON.stringify({ v }));
      return v;
    });
  }

  protected withTimeout<T>(name: string, callback: () => Promise<T>, ms?: number): Promise<T> {
    if (!globalThis.window) return callback();
    timer[name] = timer[name] ?? { args: [] };
    clearTimeout(timer[name].timer);
    return new Promise((resolve, reject) => {
      timer[name].args.push([resolve, reject]);
      timer[name].timer = setTimeout(() => {
        callback()
          .then(
            (res) => timer[name].args.forEach(([resolve]) => resolve(res)),
            (error) => timer[name].args.forEach(([, reject]) => reject(error)),
          )
          .finally(() => {
            clearTimeout(timer[name].timer);
            delete timer[name];
          });
      }, ms ?? 350);
    });
  }

  constructor(public client: AxiosInstance) {}
  // ###########################################################################

  public searchProducts = async (name: string, categoryId?: string) => {
    if (!name) return [];
    const params: { name: string; categoryId?: string } = { name };
    if (categoryId) {
      params["categoryId"] = categoryId;
    }
    return this.withCache(`searchProducts_${name}_${categoryId}`, () =>
      this.withResult<Array<ProductItemProps>>(
        this.fetch({
          url: `/api/v2/products`,
          params: params,
          baseURL: this.locationBaseURL,
        }).then((res) => {
          return res.data?.data ?? res.data ?? [];
        }),
        [],
      ),
    );
  };

  public queryProducts = async (categoryId: string) => {
    return this.withCache(`searchProducts_${categoryId}`, () =>
      this.withResult<Array<ProductItemProps>>(
        this.fetch({
          url: `/api/v2/products`,
          params: { categoryId },
          baseURL: this.locationBaseURL,
        }).then((res) => {
          return res.data?.data ?? res.data ?? [];
        }),
        [],
      ),
    );
  };

  // ###########################################################################
  public getLocationProvinces = async () => {
    return this.withCache("locationProvinces", () =>
      this.withResult<Array<Location>>(
        this.fetch({ url: `/api/v1/locations/provinces`, baseURL: this.locationBaseURL }).then(
          (res) => res.data?.data ?? res.data ?? [],
        ),
        [],
      ),
    );
  };

  public getLocationDistricts = async () => {
    return this.withCache("locationDistricts", () =>
      this.withResult<Array<Location & { locationProvinceId: number }>>(
        this.fetch({ url: `/api/v1/locations/districts`, baseURL: this.locationBaseURL }).then(
          (res) => res.data?.data ?? res.data ?? [],
        ),
        [],
      ),
    );
  };

  public getLocationAllWards = async () => {
    return this.withCache(`locationWards`, () =>
      this.withResult<Array<Location & { locationDistrictId: number }>>(
        this.fetch({
          url: `/api/v1/locations/wards`,
          baseURL: this.locationBaseURL,
        }).then((res) => res.data?.data ?? res.data ?? []),
        [],
      ),
    );
  };

  public getLocationWards = async (payload: { districtId: string }) => {
    return this.withCache(`locationWards_${payload.districtId}`, () =>
      this.withResult<Array<Location & { locationDistrictId: number }>>(
        this.fetch({
          url: `/api/v1/locations/wards/${payload.districtId}`,
          baseURL: this.locationBaseURL,
        }).then((res) => res.data?.data ?? res.data ?? []),
        [],
      ),
    );
  };

  // ###########################################################################
  public getStores = async () => {
    return this.withCache("stores", () =>
      this.withResult<Array<Store>>(
        this.fetch({ url: `/api/v1/stores`, baseURL: this.locationBaseURL }).then((res) => {
          return res.data?.data ?? res.data ?? [];
        }),
        [],
      ),
    );
  };

  // ###########################################################################
  public addToCart = async (payload: { productId: string; quantity: number }): Promise<void> => {
    const hasCart = !!this.cart;
    const key = this.cart ?? v4();
    !hasCart && this.updateCookie?.({ cart: key });
    return this.withTimeout(`addToCart:${payload.productId}`, () =>
      this.fetch({
        url: `/api/v2/carts`,
        method: "post",
        baseURL: this.cartBaseURL,
        data: { ...payload, key: key },
      }).then(
        () => {},
        () => {},
      ),
    );
  };

  public addPromotionToCart = async (payload: PromotionResult): Promise<void> => {
    const hasCart = !!this.cart;
    const key = this.cart ?? v4();
    !hasCart && this.updateCookie?.({ cart: key });
    return this.withTimeout(`addPromotionToCart:${payload.resultType}`, () =>
      this.fetch({
        url: `/api/v2/carts/promotions`,
        method: "POST",
        baseURL: this.cartBaseURL,
        data: { ...payload, key: key },
      }).then(
        () => {},
        () => {},
      ),
    );
  };

  public getCart = async (): Promise<
    Array<{ product: any; productId: string; quantity: number }>
  > => {
    const hasCart = !!this.cart;
    const key = this.cart ?? v4();
    !hasCart && this.updateCookie?.({ cart: key });
    return this.fetch({
      url: `/api/v2/carts/${key}`,
      baseURL: this.cartBaseURL,
    }).then(
      (res) => res.data?.data ?? res.data,
      () => ({ items: [] }),
    );
  };

  // ###########################################################################
  public createOrder = async (payload: OrderPayload) => {
    const hasCart = !!this.cart;
    const key = this.cart ?? v4();
    !hasCart && this.updateCookie?.({ cart: key });
    return this.withResult<OrderResponse>(
      this.fetch({
        url: `/api/v2/orders`,
        method: "post",
        baseURL: this.orderBaseURL,
        data: {
          ...payload,
          cartKey: key,
          data: {
            ...payload.data,
            saleChannelId: this.saleChannelId,
            customerInfo: { ...payload.data.customerInfo, gender: true },
          },
        },
      }).then((res) => res.data?.data ?? res.data),
    );
  };

  public checkPromotion = async (payload: CheckPromotionPayload) => {
    return this.withResult<CheckPromotionResponse>(
      this.fetch({
        url: `/api/v1/promotions/check`,
        method: "post",
        baseURL: this.orderBaseURL,
        data: {
          ...payload,
        },
      }).then((res) => {
        return res.data?.data ?? res.data;
      }),
    );
  };

  public getRecommendationPromotion = async (payload: GetRecommendationPromotionPayload) => {
    return this.withResult<GetRecommendationPromotionResponse[]>(
      this.fetch({
        url: `/api/v1/promotions/recommendation`,
        method: "post",
        baseURL: this.orderBaseURL,
        data: {
          ...payload,
        },
      }).then((res) => {
        return res.data?.data ?? res.data;
      }),
    );
  };

  public predictCod = async (payload: PredictCodPayload) => {
    const hasCart = !!this.cart;
    const key = this.cart ?? v4();
    !hasCart && this.updateCookie?.({ cart: key });
    return this.withCache(
      `${payload.storeId}${payload.toProvinceCode}${payload.toDistrictCode}${payload.toWardCode}`,
      () =>
        this.withResult<PredictCodResponse>(
          this.fetch({
            url: `/api/v2/carts/predictions/cod`,
            method: "post",
            baseURL: this.orderBaseURL,
            data: {
              ...payload,
              key,
            },
          }).then((res) => {
            return res.data?.data ?? res.data;
          }, undefined),
        ),
    );
  };

  public processOrderResponse = async (
    response: OrderResponse,
    payload?: OrderPayload,
    options?: { orderConfirmURL?: string },
  ) => {
    if (!isClientSide) return;
    const { customerId, redirectUrl, code } = ({} = response);
    const order: any = { ...response, type: "cash" };
    this.updateCookie?.({ customerId: customerId ?? undefined, orderCode: code ?? undefined });
    if (
      (payload?.paymentMethod === ORDER_PAYMENT_METHOD.DigitalWallets &&
        payload?.paymentVendor === ORDER_PAYMENT_VENDOR.VNPAY) ||
      redirectUrl?.includes("vnp_TxnRef")
    ) {
      order.type = "vnpay";

      if (redirectUrl) {
        const url = new URL(redirectUrl);
        order.txn = url.searchParams.get("vnp_TxnRef");
      }
    }

    if (
      (payload?.paymentMethod === ORDER_PAYMENT_METHOD.DigitalWallets &&
        payload?.paymentVendor === ORDER_PAYMENT_VENDOR.MOMO) ||
      redirectUrl?.includes("orderId")
    ) {
      order.type = "momo";

      if (redirectUrl) {
        const url = new URL(redirectUrl);
        order.txn = url.searchParams.get("s");
      }
    }

    if (
      (payload?.paymentMethod === ORDER_PAYMENT_METHOD.DigitalWallets &&
        payload?.paymentVendor === ORDER_PAYMENT_VENDOR.APPOTAPAY) ||
      redirectUrl?.includes("data")
    ) {
      order.type = "appota";

      if (redirectUrl) {
        const url = new URL(redirectUrl);
        order.txn = url.searchParams.get("s");
      }
    }

    localStorage.setItem("order", JSON.stringify(order));

    if (redirectUrl) {
      setTimeout(() => window.open(redirectUrl, "_self"), 250);
    } else {
      window.open(options?.orderConfirmURL ?? "/order-confirmation", "_self");
    }
  };

  public processOrderConfirmation = async () => {
    if (!isClientSide) return { isSuccess: false } as any;

    const url = new URL(window.location.href);
    let values: any = localStorage.getItem("order");
    // prettier-ignore
    try { values = JSON.parse(values) } catch (error) { }
    const results = {
      code: values.code,
      token: values.token,
      qrCode: values?.qrCode ?? undefined,
      isSuccess: false,
    };
    // NOTE: is VN Pay
    if (values?.type === "vnpay" && url.searchParams.get("vnp_TxnRef")) {
      results.isSuccess =
        values?.type === "vnpay" &&
        values?.txn === url.searchParams.get("vnp_TxnRef") &&
        // TODO: checking by order code -> call api to get order -> check status
        url.searchParams.get("vnp_ResponseCode") === "00";

      return results;
    }
    if (values?.type === "momo" && url.searchParams.get("orderId")) {
      results.isSuccess =
        values?.type === "momo" &&
        // values?.txn === url.searchParams.get("signature") &&
        // TODO: checking by order code -> call api to get order -> check status
        url.searchParams.get("resultCode") === "0";

      return results;
    }
    if (values?.type === "appota" && url.searchParams.get("data")) {
      if (!url.searchParams.get("data")) {
        results.isSuccess = false;
        return results;
      } else {
        var data = JSON.parse(atob(url.searchParams.get("data")!));
        var errorCode = data.transaction.errorCode;
        results.isSuccess = values?.type === "appota" && errorCode !== 38;
        return results;
      }
    }
    if (values?.type === "cash") {
      results.isSuccess = true;
      return results;
    }

    return results;
  };

  // ###########################################################################
  public uploadFile = async (payload: File) => {
    const formData = new FormData();
    formData.append("file", payload);
    return this.withResult<MediaResponse>(
      this.fetch({
        url: `/api/v1/upload`,
        method: "post",
        baseURL: this.mediaBaseURL,
        data: formData,
        headers: {
          Authorization: "Bearer <<tbd>>",
        },
      }).then((res) => res?.data ?? res),
    );
  };

  public sendEmail = async (payload: SendEmailPayload) => {
    return this.withResult<SendEmailResponse>(
      this.fetch({
        url: `/api/v1/openapi/contacts/send-email`,
        method: "post",
        baseURL: this.dashBaseURL,
        data: {
          ...payload,
          key: "contact",
        },
      }).then(
        (res) => {
          toast.success("Gởi liên hệ thành công.", { position: "top-right" });
          return res.data?.data ?? res.data;
        },
        (err) => {
          console.error("Error:", err);
          toast.info("Có lỗi phát sinh khi gửi liên hệ.", { position: "top-right" });
        },
      ),
    );
  };

  public getBlogPosts = async (payload: GetBlogPostsPayload) => {
    return this.withResult<GetBlogPostsResponse>(
      this.fetch({
        url: `/api/v1/sbd/flatten/inquiry`,
        method: "get",
        baseURL: this.dashBaseURL,
        params: {
          domain: payload.domain,
          page: payload.page,
          size: payload.size,
          isBlogPost: true,
        },
      }).then((res) => res.data?.data ?? res.data),
    );
  };
}

const clientFetcher = new ClientFetcher(axiosClient);

export default clientFetcher;

export enum PromotionResultType {
  None = 0,
  DiscountAmount = 1,
  DiscountPercentage = 2,
  FreeProduct = 3,
  FreeShipping = 4,
}

export interface OrderResponse {
  id: string;
  code: string;
  purchaseDate: string;
  status: number;
  state: ORDER_STATUS;
  qrCode: string;
  customerId: string;
  redirectUrl: string;
  createdAt: Date;
  totalAmount: number;
}

export interface GetRecommendationPromotionResponse {
  code: string;
  results: PromotionResult[];
}

export interface PromotionResult {
  code: string;
  promotionCampaignDetailId?: string;
  resultType: PromotionResultType;
  value: number;
  freeProductId?: string;
  conditionalProductId?: string;
}

export interface CheckPromotionResponse {
  promotionResults: PromotionResult[];
}

export interface PredictCodResponse {
  requestIdentifier: string;
  path: string[];
  totalDistance: number;
  estimatedCostOnDelivery: number;
}

export interface OrderPayload {
  cartKey?: string;
  paymentMethod: number;
  paymentVendor: number;
  data: OrderPayloadData;
}

export interface CheckPromotionPayload {
  productIds: string[];
  code: string;
}

export interface PredictCodPayload {
  toProvinceCode: string;
  toDistrictCode: string;
  toWardCode: string;
  storeId?: string;
}

export interface GetRecommendationPromotionPayload {
  productIds: string[];
}

export interface AppliedPromotion {
  code?: string;
}

export interface OrderPayloadData {
  storeId: string;
  appliedPromotion?: AppliedPromotion;
  purchaseDate: Date;
  surchages: number;
  totalAmount: number;
  paymentMethod: number;
  paymentVendor: number;
  currencyCode: number;
  saleChannelId?: string;
  orderDetails: OrderDetail[];
  customerInfo: CustomerInfo;
  orderDelivery: OrderDelivery;
}

export interface OrderDetail {
  productId: string;
  price: number;
  quantity: number;
  discount: number;
  discountRatio: number;
  totalAmount: number;
}

export interface CustomerInfo {
  [key: string]: any;
  name: string;
  gender: boolean;
  phone: string;
  locationWardId: number;
  address: string;
}

export interface OrderDelivery {
  type: number;
  price: number;
}

export interface Brand {
  id: string;
  name?: string;
  imgUrl?: string;
  description?: string;
  link?: string;
  guidelines?: Array<{ icon: string; text: string }>;
}

export interface Product {
  id: string;
  productId: string;
  code: string;
  name: string;
  slugName: string;
  description: string;
  imageUrl: string;
  basePrice: number;
  salePrice: number;
  total: number;
  stockMin: number;
  stockMax: number;
  finalRating: number;
  totalRating: number;
  totalSale: number;
  originalSalePrice: number;
  saleOffPercent: number;
  allowOutOfStockOrders: boolean;
  brand?: Brand;
  medias: ProductMedia[];
  attributes: ProductAttributes[];
  variants: ProductItemProps[];
  category?: Category;
}

export interface ProductMedia {
  id: string;
  type: number;
  value: string;
}

export interface ProductAttributes {
  id: string;
  name: string;
  value: string;
}

export interface Category {
  id: string;
  code: string;
  name: string;
  description: string;
  parentId?: string;
}

export interface MediaResponse {
  data: string;
}

export interface SendEmailPayload {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export interface SendEmailResponse {}

export interface GetBlogPostsPayload {
  domain: string;
  page: number;
  size: number;
}

export interface BlogPostItem {
  id?: number;
  name: string;
  description?: string;
  thumbnail?: string;
  slug: string;
  date?: string;
  author?: string;
  category?: string;
  tags?: string | string[];
  lang?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export interface GetBlogPostsResponse {
  data: BlogPostItem[];
  paging?: {
    total: number;
    page: number;
    size: number;
  };
}
